import { type ComponentPropsWithRef, forwardRef, type ReactNode } from "react";
import { Input } from "@/core/shared/v2/ui/input";
import { Label } from "@/core/shared/v2/ui/label";

export interface TextFieldProps extends Omit<ComponentPropsWithRef<"input">, "required"> {
  label?: ReactNode;
  error?: string;
  helperText?: ReactNode;
  /** Renders the `*` asterisk in the label. Native `required` is intentionally not
   *  forwarded — validation stays yup-only (no native browser constraint popups). */
  required?: boolean;
}

/** Label + v2 Input + error/helper text. Forwards ref so it wires to RHF via `ref={register}`. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, helperText, required, name, id, ...props }, ref) => {
    const fieldId = id ?? name;
    const helperTextId = helperText && fieldId ? `${fieldId}-helper-text` : undefined;
    const errorId = error && fieldId ? `${fieldId}-error` : undefined;
    const describedBy = [helperTextId, errorId].filter(Boolean).join(" ") || undefined;

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <Label htmlFor={fieldId}>
            {label}
            {required ? " *" : ""}
          </Label>
        )}
        <Input ref={ref} id={fieldId} name={name} aria-invalid={!!error} aria-describedby={describedBy} {...props} />
        {helperText && (
          <p id={helperTextId} className="text-muted-foreground text-sm">
            {helperText}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="font-medium text-destructive text-sm">
            {error}
          </p>
        )}
      </div>
    );
  },
);

TextField.displayName = "TextField";
